import { useMemoizedFn } from 'ahooks';
import { useStore } from '@/app/providers/store';
import type { ModalConfigMap, ModalMethods, ModalType } from '../types';

export const useGlobalModal = <T extends ModalType>(type: T): ModalMethods<T> => {
  // 只选择 store 的方法，这些方法是稳定的（在 store 创建时定义，不会改变）
  // 这样即使其他弹窗状态变化，只要这些方法引用不变，就不会触发重新渲染
  const { openModal, closeModal, isModalOpen, getModalState, updateModalConfig } = useStore((state) => ({
    openModal: state.openModal,
    closeModal: state.closeModal,
    isModalOpen: state.isModalOpen,
    getModalState: state.getModalState,
    updateModalConfig: state.updateModalConfig,
  }));

  const open = useMemoizedFn((props?: Omit<ModalConfigMap[T], 'open'>) => {
    openModal(type, props);
  });

  const close = useMemoizedFn(() => {
    closeModal(type);
  });

  const isOpen = useMemoizedFn(() => {
    return isModalOpen(type);
  });

  const getState = useMemoizedFn(() => {
    return getModalState(type);
  });

  const updateState = useMemoizedFn((props: Partial<ModalConfigMap[T]>) => {
    updateModalConfig(type, props);
  });

  return {
    open,
    close,
    isOpen,
    getState,
    updateState,
  };
};
